import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { getMenuItems } from "../../config/navigation";

const OPEN_GLOBAL_CREATE_CONTACT_MODAL_EVENT = "dana:open-create-contact-modal";

const parseStoredJson = (key) => {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const normalizeTargetPath = (targetPath = "") => String(targetPath || "").split("?")[0];

const isActivePath = (pathname, targetPath) => {
  const normalizedTarget = normalizeTargetPath(targetPath);
  return pathname === normalizedTarget || pathname.startsWith(`${normalizedTarget}/`);
};

const getSubmenuKeys = (items = []) =>
  items
    .filter((item) => Array.isArray(item.children) && item.children.length)
    .map((item) => item.key);

const buildSubmenuState = (items = []) =>
  getSubmenuKeys(items).reduce((acc, key) => ({ ...acc, [key]: false }), {});

function Sidebar({ isCollapsed = false, onToggleCollapse, onNavigate = () => {} }) {
  const { t } = useTranslation();
  const location = useLocation();
  const role = useMemo(() => {
    const extClass = parseStoredJson("Extand_Class");
    return Array.isArray(extClass) ? extClass?.[0]?.UserRole || "" : "";
  }, []);
  const menuItems = useMemo(() => getMenuItems(role, t), [role, t]);
  const settingsItem = useMemo(
    () => menuItems.find((item) => item.key === "settings") || null,
    [menuItems]
  );
  const primaryItems = useMemo(
    () => menuItems.filter((item) => item.key !== "settings"),
    [menuItems]
  );

  const submenuKeys = useMemo(() => getSubmenuKeys(menuItems), [menuItems]);
  const [submenuOpen, setSubmenuOpen] = useState(() => buildSubmenuState(menuItems));

  useEffect(() => {
    setSubmenuOpen((current) =>
      submenuKeys.reduce((acc, key) => ({ ...acc, [key]: Boolean(current[key]) }), {})
    );
  }, [submenuKeys]);

  const closeAllSubmenus = () => {
    setSubmenuOpen((current) =>
      Object.keys(current).reduce((acc, key) => ({ ...acc, [key]: false }), {})
    );
  };
  const handleNavigate = () => {
    closeAllSubmenus();
    onNavigate();
  };

  const openSubmenu = (menuKey) => {
    setSubmenuOpen((current) => {
      const next = Object.keys(current).reduce((acc, key) => ({ ...acc, [key]: false }), {});
      if (Object.prototype.hasOwnProperty.call(next, menuKey)) {
        next[menuKey] = true;
      }
      return next;
    });
  };

  const closeSubmenu = (menuKey) => {
    setSubmenuOpen((current) => ({ ...current, [menuKey]: false }));
  };

  const toggleSubmenu = (menuKey) => {
    setSubmenuOpen((current) => {
      const next = Object.keys(current).reduce((acc, key) => ({ ...acc, [key]: false }), {});
      if (Object.prototype.hasOwnProperty.call(next, menuKey)) {
        next[menuKey] = !current[menuKey];
      }
      return next;
    });
  };

  const renderMenuItem = (item) => {
    if (!item.children) {
      return (
        <li key={item.key} className="dana-sidebar-entry">
          <NavLink
            to={item.to}
            title={item.label}
            aria-label={item.label}
            data-tooltip={isCollapsed ? item.label : ""}
            onClick={handleNavigate}
            className={({ isActive }) =>
              `dana-sidebar-link dana-sidebar-tooltip-target ${isActive ? "is-active" : ""}`
            }
          >
            <span className="dana-sidebar-link-icon">
              <Icon name={item.icon} />
            </span>
            <span className="dana-sidebar-link-label" title={item.label}>
              {item.label}
            </span>
          </NavLink>
        </li>
      );
    }

    const hasActiveChild = item.children.some((child) => isActivePath(location.pathname, child.to));
    const isOpen = submenuOpen[item.key];
    const primaryChildKey =
      item.key === "templates" ? "create-template" : item.key === "contactbook" ? "create-contact" : "";
    const primaryChild = primaryChildKey
      ? item.children.find((child) => child.key === primaryChildKey) || null
      : null;

    return (
      <li
        key={item.key}
        className={`dana-sidebar-entry ${item.key === "settings" ? "is-bottom-anchored" : ""}`}
        onMouseEnter={() => openSubmenu(item.key)}
        onMouseLeave={() => closeSubmenu(item.key)}
      >
        <button
          type="button"
          className={`dana-sidebar-link dana-sidebar-toggle dana-sidebar-tooltip-target ${hasActiveChild ? "is-active" : ""}`}
          onClick={() => toggleSubmenu(item.key)}
          aria-expanded={isOpen ? "true" : "false"}
          aria-controls={`submenu-${item.key}`}
          aria-label={item.label}
          data-tooltip={isCollapsed ? item.label : ""}
        >
          <span className="dana-sidebar-link-icon">
            <Icon name={item.icon} />
          </span>
          <span className="dana-sidebar-link-label" title={item.label}>
            {item.label}
          </span>
          <span className="dana-sidebar-link-caret">
            <Icon name={isOpen ? "chevronDown" : "chevronRight"} />
          </span>
        </button>
        {isOpen ? (
          <div id={`submenu-${item.key}`} className="dana-sidebar-submenu-popover">
            {primaryChild ? (
              item.key === "contactbook" ? (
                <button
                  type="button"
                  className="dana-submenu-primary-btn w-full border-0"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_CREATE_CONTACT_MODAL_EVENT));
                    handleNavigate();
                  }}
                >
                  {primaryChild.label}
                </button>
              ) : (
                <NavLink
                  to={primaryChild.to}
                  className={({ isActive }) =>
                    `dana-submenu-primary-btn ${isActive ? "is-active" : ""}`
                  }
                  onClick={handleNavigate}
                >
                  {primaryChild.label}
                </NavLink>
              )
            ) : null}
            <ul className="dana-sidebar-submenu">
              {item.children
                .filter((child) => !primaryChild || child.key !== primaryChild.key)
                .map((child) => (
                <li key={child.key}>
                  <NavLink
                    to={child.to}
                    className={({ isActive }) => `dana-sidebar-sublink ${isActive ? "is-active" : ""}`}
                    onClick={handleNavigate}
                  >
                    <span className="dana-sidebar-link-icon">
                      <Icon name={child.icon} />
                    </span>
                    <span className="dana-sidebar-link-label" title={child.label}>
                      {child.label}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <nav
      className={`dana-sidebar ${isCollapsed ? "is-collapsed" : ""}`}
      aria-label={t("new-ui.sidebar.navigation", { defaultValue: "DANASign navigation" })}
    >
      <div className="dana-sidebar-head-row">
        <button
          type="button"
          className="dana-sidebar-collapse"
          onClick={onToggleCollapse}
          aria-label={
            isCollapsed
              ? t("new-ui.sidebar.expand-menu", { defaultValue: "Expand menu" })
              : t("new-ui.sidebar.collapse-menu", { defaultValue: "Collapse menu" })
          }
          title={
            isCollapsed
              ? t("new-ui.sidebar.expand-menu", { defaultValue: "Expand menu" })
              : t("new-ui.sidebar.collapse-menu", { defaultValue: "Collapse menu" })
          }
        >
          <Icon name={isCollapsed ? "chevronRight" : "chevronLeft"} />
        </button>
      </div>
      <ul className="dana-sidebar-list">
        {primaryItems.map((item) => renderMenuItem(item))}
      </ul>
      {settingsItem ? (
        <div className="dana-sidebar-bottom">
          <ul className="dana-sidebar-list dana-sidebar-list-bottom">
            {renderMenuItem(settingsItem)}
          </ul>
        </div>
      ) : null}
    </nav>
  );
}

export default Sidebar;
